import { jsxs, jsx, Fragment } from 'react/jsx-runtime';
import { Spinner } from '@flex-design-system/fx';
import { colors } from '@flex-design-system/fx-colors';
import { styled } from '@flex-design-system/stitches-react';
import { Flex } from '@flex-design-system/utils-style';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { useLexicalNodeSelection } from '@lexical/react/useLexicalNodeSelection';
import { mergeRegister } from '@lexical/utils';
import { $isNodeSelection, $getSelection, $getNodeByKey, CLICK_COMMAND, COMMAND_PRIORITY_LOW, CUT_COMMAND, KEY_DELETE_COMMAND, KEY_BACKSPACE_COMMAND } from 'lexical';
import { useRef, useState, useCallback, useEffect } from 'react';
import { $isImageNode } from './ImageNode.mjs';
import ImageResizer from './ImageResizer.mjs';

const MAX_WIDTH_FALLBACK = 500;
function ImageComponent({
  nodeKey,
  src,
  width,
  height,
  maxWidth: maxWidthFromProps,
  style,
  ...props
}) {
  const [editor] = useLexicalComposerContext();
  const imageRef = useRef(null);
  const [isLoading, setLoading] = useState(false);
  const [isResized, setIsResized] = useState(false);
  const [isSelected, setSelected, clearSelection] = useLexicalNodeSelection(nodeKey);
  const useInheritSize = typeof width === "string" || typeof height === "string";
  const initialRatio = !useInheritSize ? height / width : 0;
  const maxWidth = maxWidthFromProps ?? editor.getRootElement()?.getBoundingClientRect().width ?? MAX_WIDTH_FALLBACK;
  const maxHeight = isResized || useInheritSize ? "none" : maxWidth * initialRatio;
  const handleDelete = useCallback(
    (payload) => {
      if (isSelected && $isNodeSelection($getSelection())) {
        payload.preventDefault();
        const node = $getNodeByKey(nodeKey);
        if ($isImageNode(node)) {
          node.remove();
        }
        setSelected(false);
      }
      return false;
    },
    [isSelected, nodeKey, setSelected]
  );
  useEffect(() => {
    setLoading(true);
  }, []);
  useEffect(() => {
    return mergeRegister(
      editor.registerCommand(
        CLICK_COMMAND,
        (event) => {
          if (event.target === imageRef.current) {
            setSelected(true);
            return true;
          }
          return false;
        },
        COMMAND_PRIORITY_LOW
      ),
      editor.registerCommand(
        CUT_COMMAND,
        () => {
          if (isSelected) {
            setTimeout(() => {
              clearSelection();
            }, 0);
          }
          return false;
        },
        COMMAND_PRIORITY_LOW
      ),
      editor.registerCommand(
        KEY_DELETE_COMMAND,
        handleDelete,
        COMMAND_PRIORITY_LOW
      ),
      editor.registerCommand(
        KEY_BACKSPACE_COMMAND,
        handleDelete,
        COMMAND_PRIORITY_LOW
      )
    );
  }, [editor, setSelected, clearSelection, isSelected, nodeKey, handleDelete]);
  const handleResizeStart = () => {
    setIsResized(true);
  };
  const handleResizeEnd = (newWidth, newHeight) => {
    editor.update(() => {
      const node = $getNodeByKey(nodeKey);
      if ($isImageNode(node)) {
        node.setSize(newWidth, newHeight);
      }
    });
  };
  return /* @__PURE__ */ jsxs(Container, { children: [
    isLoading && /* @__PURE__ */ jsx(Skeleton, { width, height }),
    /* @__PURE__ */ jsx(
      Image,
      {
        ...props,
        ref: imageRef,
        src,
        onLoad: () => setLoading(false),
        onError: () => setLoading(false),
        width: isLoading ? 0 : width,
        height,
        style: {
          ...style,
          maxWidth: "100%",
          maxHeight
        },
        focused: isSelected
      }
    ),
    isSelected && /* @__PURE__ */ jsx(
      ImageResizer,
      {
        imageTarget: imageRef.current,
        maxWidth,
        onResizeStart: handleResizeStart,
        onResizeEnd: handleResizeEnd
      }
    )
  ] });
}
function Skeleton({ width, height }) {
  if (typeof width === "string" || typeof height === "string") {
    return null;
  }
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(SkeletonImage, { width, height }),
    /* @__PURE__ */ jsx(SpinnerContainer, { children: /* @__PURE__ */ jsx(Spinner, { size: "small" }) })
  ] });
}
const Container = styled("div", {
  display: "inline-block",
  position: "relative",
  width: "fit-content",
  height: "fit-content"
});
const Image = styled("img", {
  variants: {
    focused: {
      true: {
        border: "2px solid $cosmicBlue"
      }
    }
  }
});
const SkeletonImage = styled("img", {
  maxWidth: "100%",
  height: "auto",
  opacity: 0
});
const SpinnerContainer = styled(Flex.Center, {
  position: "absolute",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  backgroundColor: colors.blackAlpha012
});

export { ImageComponent as default };
//# sourceMappingURL=ImageComponent.mjs.map
